import { createFileRoute, Outlet } from '@tanstack/react-router'
import { trpc } from '@/main'
import { reportSearchDeps, prefetchReport } from '@/hooks/use-report'

/**
 * Layout for a selected functional group (`/orgs/$orgId/groups/$groupId/*`). It renders the animated
 * tab Outlet (real content), so per AGENTS.md it OWNS the orgId/groupId-keyed chrome its subtree reads
 * — the groups list (`getGroups`), the group users list (`getOrgGroupUsers`, read by the users panel in
 * orgs.tsx), and the report data shared by all three tab children. The board/distribution leaves keep
 * only their own tab-specific query (`getOrgGroupProfile`); the report leaf has no loader.
 *
 * The `AnimatedOutlet` now lives in the parent `orgs.tsx` (wrapping the `<Outlet/>` that renders this
 * layout) so the enter animation plays once when the report panel first appears and never re-animates on
 * tab/group switches; this layout renders a plain `<Outlet/>` for its tab children.
 */
export const Route = createFileRoute('/_app/orgs/$orgId/groups/$groupId')({
  loaderDeps: ({ search }) => reportSearchDeps(search),
  loader: ({ context, deps, params }) => {
    context.queryClient.prefetchQuery(trpc.orgs.getGroups.queryOptions(params.orgId))
    context.queryClient.prefetchQuery(
      trpc.orgs.getOrgGroupUsers.queryOptions({
        orgId: params.orgId,
        groupId: params.groupId,
        orgType: deps.orgType,
      }),
    )
    prefetchReport(context.queryClient, deps, params)
  },
  component: RouteComponent,
  // No pendingComponent: this passthrough layout just renders <AnimatedOutlet/>. The data-cold case is
  // covered by the parent orgs.tsx (<Suspense fallback={ReportPanelSkeleton}>), and each tab leaf
  // (report/board/distribution) owns its own tab-specific skeleton — a generic one here would mismatch.
})

function RouteComponent() {
  return <Outlet />
}
